import {
  downloadAudioWithCustomNameRequest,
  downloadMultimediaRequest,
} from "@services/multimedia/multimediaApi";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  createBatchAudioZip,
  sanitizeBatchAudioFilenamePart,
} from "@utils/mode_tool/batchAudioZip";
import { useMemo, useState } from "react";

function readLocalizedName(value = {}, preferredLanguage = "") {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const languageEntries = value.flatMap((item) =>
      Object.entries(item || {}).filter(([key]) => key !== "id"),
    );
    const preferred = languageEntries.find(
      ([language]) => language.toLowerCase() === preferredLanguage,
    );
    const english = languageEntries.find(([language]) => language.toLowerCase() === "en");
    const selected = preferred || english || languageEntries[0];
    return String(selected?.[1]?.value ?? selected?.[1] ?? "").trim();
  }
  if (!value || typeof value !== "object") return "";
  const localized = value[preferredLanguage] || value.en || Object.values(value).find(Boolean);
  return String(localized?.value ?? localized ?? "").trim();
}

function getPointAudioAssets(point = {}) {
  const assets = [];
  const add = (media = {}, languageHint = "") => {
    if (!media || typeof media !== "object") return;
    const url = String(media.url || media.audioUrl || "").trim();
    const id = String(media.id || media.multimediaId || "").trim();
    if (!url && !id) return;
    assets.push({
      ...media,
      id,
      url,
      language: String(media.language || media.languageIso || languageHint || "").toLowerCase(),
    });
  };

  (Array.isArray(point?.multimedia) ? point.multimedia : []).forEach((media) => {
    if (resolveMediaKind(media) === "audio") add(media);
  });
  (Array.isArray(point?.audios) ? point.audios : []).forEach((media) => add(media));

  const aiAudio = point?.aiAudioDescription ?? point?.ai_audio_description;
  const aiAudioEntries = Array.isArray(aiAudio)
    ? aiAudio.flatMap((item) => Object.entries(item || {}).filter(([key]) => key !== "id"))
    : Object.entries(aiAudio || {});
  aiAudioEntries.forEach(([language, localizedAudio]) => {
    add(localizedAudio?.audio || localizedAudio, language);
  });

  const pointAssets = new Map();
  assets.forEach((media) => {
    const key = media.id ? `id:${media.id}` : `url:${media.url}`;
    if (!pointAssets.has(key)) pointAssets.set(key, media);
  });
  return Array.from(pointAssets.values());
}

function getAudioExtension(media = {}, blob = {}) {
  const mimeType = String(blob.type || media.mimeType || "").toLowerCase();
  const mimeExtensions = {
    "audio/mpeg": "mp3",
    "audio/mp3": "mp3",
    "audio/wav": "wav",
    "audio/x-wav": "wav",
    "audio/ogg": "ogg",
    "audio/aac": "aac",
    "audio/mp4": "m4a",
    "audio/x-m4a": "m4a",
    "audio/flac": "flac",
  };
  if (mimeExtensions[mimeType]) return mimeExtensions[mimeType];
  const urlExtension = String(media.url || "")
    .split("?")[0]
    .match(/\.([a-z0-9]{2,5})$/i)?.[1];
  return urlExtension || "mp3";
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}

export default function useBatchAudioDownload({
  selectedMode = "",
  activeLanguageIso = "",
  modePoints = [],
  walkingPointsByRoute = {},
  tourName = "",
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  function clearFeedback() {
    setStatus("");
    setError("");
  }
  const mode = String(selectedMode || "").toLowerCase();
  const language = String(activeLanguageIso || "").toLowerCase().trim();
  const points = useMemo(() => {
    if (mode === "walking") {
      return Object.values(walkingPointsByRoute || {}).flatMap((routePoints) =>
        Array.isArray(routePoints) ? routePoints : [],
      );
    }
    return Array.isArray(modePoints) ? modePoints : [];
  }, [mode, modePoints, walkingPointsByRoute]);

  const audioEntries = useMemo(
    () => points.flatMap((point, index) =>
      getPointAudioAssets(point)
        .filter((media) => media.language === language)
        // A point has one replaceable manual audio slot per language.
        // Prefer its regular multimedia audio so the downloaded ZIP round-trips through upload.
        .slice(0, 1)
        .map((media) => ({
          media,
          pointId: String(point?.id || point?.internalId || `point-${index + 1}`),
          pointName: readLocalizedName(point?.name, language) || `Point-${index + 1}`,
        })),
    ),
    [language, points],
  );

  async function downloadBatchAudios() {
    if (isDownloading) return;
    setError("");
    setStatus("");
    if (!audioEntries.length) {
      setError(
        `No audio files were found for ${language.toUpperCase()} in the current route points.`,
      );
      return;
    }

    setIsDownloading(true);
    setStatus(`Downloading 0 of ${audioEntries.length} audios…`);
    try {
      const downloaded = new Array(audioEntries.length);
      const failures = [];
      let nextIndex = 0;
      let completed = 0;
      const worker = async () => {
        while (nextIndex < audioEntries.length) {
          const index = nextIndex;
          nextIndex += 1;
          const entry = audioEntries[index];
          try {
            const response = entry.media.url
              ? await downloadAudioWithCustomNameRequest({
                  url: resolveMediaUrl(entry.media.url),
                  language: entry.media.language || "en",
                  name: entry.pointName,
                })
              : await downloadMultimediaRequest(entry.media.id);
            const blob = response?.data;
            if (!(blob instanceof Blob) || blob.size === 0) {
              throw new Error("The audio response was empty.");
            }
            downloaded[index] = { ...entry, blob };
          } catch (downloadError) {
            failures.push({
              pointName: entry.pointName,
              message: downloadError?.message || "Download failed",
            });
          } finally {
            completed += 1;
            setStatus(`Downloading ${completed} of ${audioEntries.length} audios…`);
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(4, audioEntries.length) }, worker),
      );

      if (failures.length) {
        const failedPoints = failures.slice(0, 3).map(({ pointName }) => pointName).join(", ");
        throw new Error(
          `Could not download ${failures.length} of ${audioEntries.length} audios (${failedPoints}). No ZIP was created.`,
        );
      }

      const safeTourName = sanitizeBatchAudioFilenamePart(tourName || "tour");
      const fileBases = downloaded.map(({ pointId, pointName }) =>
        [pointId, language, pointName, mode, safeTourName]
          .map(sanitizeBatchAudioFilenamePart)
          .join("-"),
      );
      const duplicateCounts = new Map();
      const files = downloaded.map((entry, index) => {
        const extension = getAudioExtension(entry.media, entry.blob);
        const base = fileBases[index];
        const totalWithSameBase = fileBases.filter((candidate) => candidate === base).length;
        const languagePart = totalWithSameBase > 1 && entry.media.language
          ? `-${sanitizeBatchAudioFilenamePart(entry.media.language)}`
          : "";
        const collisionKey = `${base}${languagePart}`;
        const occurrence = (duplicateCounts.get(collisionKey) || 0) + 1;
        duplicateCounts.set(collisionKey, occurrence);
        const uniqueSuffix = occurrence > 1 ? `-${occurrence}` : "";
        return {
          name: `${base}${languagePart}${uniqueSuffix}.${extension}`,
          blob: entry.blob,
        };
      });
      const zipBlob = await createBatchAudioZip(files);
      const zipName = `${sanitizeBatchAudioFilenamePart(language)}-${sanitizeBatchAudioFilenamePart(mode)}-${safeTourName}-batch-audios.zip`;
      triggerBlobDownload(zipBlob, zipName);
      setStatus(`${files.length} audios included in ${zipName}.`);
    } catch (downloadError) {
      setError(downloadError?.message || "Could not create the batch audio ZIP.");
      setStatus("");
    } finally {
      setIsDownloading(false);
    }
  }

  return { downloadBatchAudios, isDownloading, status, error, clearFeedback };
}
